import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { ITruckGroup, ITruckGroupListDTO, defaultValue } from 'app/shared/model/truck-group.model';
import { ITruckImplement, TruckImplementListDTO } from 'app/shared/model/truck-implement.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<ITruckGroup> = {
  loading: false,
  errorMessage: null,
  entities: [] as ITruckGroupListDTO[],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  entitiesAlt: null,
  entitiesAlt2: null,
};

const apiUrl = 'api/truck-groups';

let cancelTokenSource = axios.CancelToken.source();

export const getEntities = createAsyncThunk(
  'truckGroup/fetch_entity_list',
  async ({ page, size, sort, companyId }: IQueryParams & { companyId?: number }) => {
    const companyParam = companyId ? `&companyId=${companyId}` : '';
    const requestUrl = `${apiUrl}?page=${page ?? 0}&size=${size ?? 20}${sort ? `&sort=${sort}` : ''}${companyParam}&cacheBuster=${new Date().getTime()}`;

    cancelTokenSource.cancel('New request started.');
    cancelTokenSource = axios.CancelToken.source();

    return axios.get<ITruckGroup[]>(requestUrl, { cancelToken: cancelTokenSource.token });
  },
  { serializeError: serializeAxiosError }
);

export const getSelectorEntities = createAsyncThunk(
  'truckGroup/fetch_selector_entities',
  async ({ page = 0, size = 999, sort = 'name,asc', companyId }: IQueryParams & { companyId?: number } = {}) => {
    const params = new URLSearchParams({ page: `${page}`, size: `${size}`, sort });
    if (companyId !== undefined) params.append('companyId', `${companyId}`);
    params.append('cacheBuster', `${new Date().getTime()}`);
    return axios.get<ITruckGroup[]>(`${apiUrl}?${params.toString()}`);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'truckGroup/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITruckGroup>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getDefaultGroup = createAsyncThunk(
  'truckGroup/fetch_default',
  async () => {
    const requestUrl = `${apiUrl}/default`;
    return axios.get<ITruckGroup>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'truckGroup/create_entity',
  async (entity: ITruckGroup) => {
    return axios.post<ITruckGroup>(apiUrl, cleanEntity(entity));
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'truckGroup/update_entity',
  async (entity: ITruckGroup) => {
    return axios.put<ITruckGroup>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'truckGroup/delete_entity',
  async ({ id, companyId }: { id: string | number; companyId?: number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITruckGroup>(requestUrl);
    thunkAPI.dispatch(getEntities({ companyId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const setGroupAlerts = createAsyncThunk(
  'truckGroup/set_alerts',
  async ({ groupId, alertIds }: { groupId: number; alertIds: number[] }) => {
    return axios.put<ITruckGroup>(`${apiUrl}/${groupId}/alerts`, alertIds);
  },
  { serializeError: serializeAxiosError }
);

export const addGroupAlerts = createAsyncThunk(
  'truckGroup/add_alerts',
  async ({ groupId, alertIds }: { groupId: number; alertIds: number[] }) => {
    return axios.post<ITruckGroup>(`${apiUrl}/${groupId}/alerts`, alertIds);
  },
  { serializeError: serializeAxiosError }
);

export const removeGroupAlerts = createAsyncThunk(
  'truckGroup/remove_alerts',
  async ({ groupId, alertIds }: { groupId: number; alertIds: number[] }) => {
    return axios.delete<ITruckGroup>(`${apiUrl}/${groupId}/alerts`, { data: alertIds });
  },
  { serializeError: serializeAxiosError }
);

export const setGroupVirtualFences = createAsyncThunk(
  'truckGroup/set_virtual_fences',
  async ({ groupId, virtualFenceIds }: { groupId: number; virtualFenceIds: number[] }) => {
    return axios.put<ITruckGroup>(`${apiUrl}/${groupId}/fences`, virtualFenceIds);
  },
  { serializeError: serializeAxiosError }
);

export const addGroupVirtualFences = createAsyncThunk(
  'truckGroup/add_virtual_fences',
  async ({ groupId, virtualFenceIds }: { groupId: number; virtualFenceIds: number[] }) => {
    return axios.post<ITruckGroup>(`${apiUrl}/${groupId}/fences`, virtualFenceIds);
  },
  { serializeError: serializeAxiosError }
);

export const removeGroupVirtualFences = createAsyncThunk(
  'truckGroup/remove_virtual_fences',
  async ({ groupId, virtualFenceIds }: { groupId: number; virtualFenceIds: number[] }) => {
    return axios.delete<ITruckGroup>(`${apiUrl}/${groupId}/fences`, { data: virtualFenceIds });
  },
  { serializeError: serializeAxiosError }
);

export const getGroupVirtualFenceIds = createAsyncThunk(
  'truckGroup/fetch_virtual_fence_ids',
  async (groupId: number) => {
    return axios.get<number[]>(`${apiUrl}/${groupId}/fences`);
  },
  { serializeError: serializeAxiosError }
);

export const setGroupPolygonFences = createAsyncThunk(
  'truckGroup/set_polygon_fences',
  async ({ groupId, polygonIds }: { groupId: number; polygonIds: number[] }) => {
    return axios.put<ITruckGroup>(`${apiUrl}/${groupId}/polygon-fences`, polygonIds);
  },
  { serializeError: serializeAxiosError }
);

export const getGroupPolygonFenceIds = createAsyncThunk(
  'truckGroup/fetch_polygon_fence_ids',
  async (groupId: number) => {
    return axios.get<number[]>(`${apiUrl}/${groupId}/polygon-fences`);
  },
  { serializeError: serializeAxiosError }
);

export interface IFenceTruckSyncStatus {
  licensePlate: string;
  syncStatus: 'PENDING_SYNC' | 'SYNCED' | 'PENDING_DELETE';
}

export interface IFenceSyncStatus {
  fenceId: number;
  fenceName: string;
  fenceType?: 'CIRCLE' | 'POLYGON';
  trucks: IFenceTruckSyncStatus[];
}

export const getGroupFenceSyncStatus = createAsyncThunk(
  'truckGroup/fetch_fence_sync_status',
  async (groupId: number) => {
    return axios.get<IFenceSyncStatus[]>(`${apiUrl}/${groupId}/fences/sync-status`);
  },
  { serializeError: serializeAxiosError }
);

export const assignTrucks = createAsyncThunk(
  'truckGroup/assign_trucks',
  async ({ groupId, truckIds }: { groupId: number; truckIds: number[] }) => {
    return axios.put<void>(`${apiUrl}/${groupId}/trucks`, truckIds);
  },
  { serializeError: serializeAxiosError }
);

export const getGroupTrucks = createAsyncThunk(
  'truckGroup/fetch_trucks',
  async (groupId: number) => {
    const requestUrl = `${apiUrl}/${groupId}/trucks`;
    return axios.get<ITruckImplement[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getGroupTruckDetails = createAsyncThunk(
  'truckGroup/fetch_truck_details',
  async (groupId: number) => {
    return axios.get<TruckImplementListDTO[]>(`${apiUrl}/${groupId}/trucks/details`);
  },
  { serializeError: serializeAxiosError }
);

export const getGroupTruckIds = createAsyncThunk(
  'truckGroup/fetch_truck_ids',
  async (groupId: number) => {
    return axios.get<number[]>(`${apiUrl}/${groupId}/trucks`);
  },
  { serializeError: serializeAxiosError }
);

export interface ITruckGroupAlertDetails {
  id?: number;
  name?: string | null;
  alertType?: string | null;
  alertOperator?: string | null;
  value?: number | null;
  valueInRangeMin?: number | null;
  valueInRangeMax?: number | null;
  sensorName?: string | null;
  activated?: boolean | null;
  alertCluster?: string | null;
}

export const getGroupAlertDetails = createAsyncThunk(
  'truckGroup/fetch_alert_details',
  async (groupId: number) => {
    return axios.get<ITruckGroupAlertDetails[]>(`${apiUrl}/${groupId}/alerts/details`);
  },
  { serializeError: serializeAxiosError }
);

export interface ITruckGroupSaveDTO {
  id?: number | null;
  name: string;
  description?: string | null;
  company?: { id: number } | null;
  alertIds?: number[] | null;
  truckIds?: number[] | null;
  virtualFenceIds?: number[] | null;
}

export const createEntityWithDetails = createAsyncThunk(
  'truckGroup/create_entity_with_details',
  async (dto: ITruckGroupSaveDTO) => {
    return axios.post<ITruckGroup>(`${apiUrl}/save`, dto);
  },
  { serializeError: serializeAxiosError }
);

export const updateEntityWithDetails = createAsyncThunk(
  'truckGroup/update_entity_with_details',
  async (dto: ITruckGroupSaveDTO) => {
    return axios.put<ITruckGroup>(`${apiUrl}/${dto.id}/save`, dto);
  },
  { serializeError: serializeAxiosError }
);

export const TruckGroupSlice = createEntitySlice({
  name: 'truckGroup',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    clearEntities(state) {
      state.entities = [];
      state.loading = true;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getDefaultGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getSelectorEntities.fulfilled, (state, action) => {
        state.entities = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getGroupVirtualFenceIds.fulfilled, (state, action) => {
        state.loading = false;
        state.entitiesAlt = action.payload.data;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const payload = action.payload as { data: ITruckGroup[]; headers: Record<string, string> };
        if (!payload) return state;
        return {
          ...state,
          loading: false,
          entities: payload.data,
          totalItems: parseInt(payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(
        isFulfilled(
          createEntity,
          updateEntity,
          setGroupAlerts,
          addGroupAlerts,
          removeGroupAlerts,
          setGroupVirtualFences,
          addGroupVirtualFences,
          removeGroupVirtualFences
        ),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data;
        }
      )
      .addMatcher(isFulfilled(createEntityWithDetails, updateEntityWithDetails), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getGroupTruckIds), (state, action) => {
        state.loading = false;
        state.entitiesAlt2 = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getDefaultGroup), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(
        isPending(
          createEntity,
          updateEntity,
          deleteEntity,
          setGroupAlerts,
          addGroupAlerts,
          removeGroupAlerts,
          setGroupVirtualFences,
          addGroupVirtualFences,
          removeGroupVirtualFences,
          assignTrucks,
          createEntityWithDetails,
          updateEntityWithDetails
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset, clearEntities } = TruckGroupSlice.actions;

export default TruckGroupSlice.reducer;
